import { UserDTOInternalRolesEnum, UserDTOUserTypeIdEnum } from '@reposit/api-client';
import { lowerCase, startCase } from 'lodash';
import moment from 'moment';
import React, { Fragment, useEffect } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { getBreakpoint } from '../../base/style';
import Button from '../../components/Button';
import FlashMessage from '../../components/FlashMessage';
import IdentifierValue from '../../components/IdentifierValue';
import { FullPageLoader } from '../../components/Loading';
import RepositCard from '../../components/Reposit/RepositCard';
import { ResetPasswordModal } from '../../components/ResetPasswordModal';
import { Table, Tbody, Td, Th, Tr } from '../../components/Table';
import { Caption, Header2, Header4 } from '../../components/Typography';
import { useAdminDocumentTitle } from '../../hooks/use-document-title';
import { hasInternalRole } from '../../redux/account/account.selectors';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import { setCurrentModal } from '../../redux/modal/modal.actions';
import { getCurrentModal } from '../../redux/modal/modal.selectors';
import { AppState } from '../../redux/root.reducer';
import { getUserById } from '../../redux/selectors/user.selectors';
import {
  BLOCK_TENANT_STORE_KEY,
  FETCH_USER_BY_ID_STORE_KEY,
  fetchBlockedTenantRequested,
  fetchOrganizationUsersByUserIdRequested,
  fetchUserByIdRequested,
  RESET_USER_PASSWORD_STORE_KEY,
  setIsEditModalOpen,
  UNBLOCK_TENANT_STORE_KEY,
  UPDATE_USER_BY_ID_STORE_KEY,
} from '../../redux/user/user.actions';
import {
  getBlockedTenant,
  getHasFetchedBlockedTenant,
  getIsEditModalOpen,
  getIsTenantBlocked,
  getOrganizationUsersByUserId,
} from '../../redux/user/user.selectors';
import { useFlashMessage } from '../FlashMessage';
import { RolePill } from '../OrganizationUsers';
import { BlockTenantModal } from './block-tenant.modal';
import { resolveBlockedTenantAction } from './blocked-tenant-action';
import { BlockedTenantBanner } from './blocked-tenant-banner';
import { EditUserModal } from './edit-user.modal';
import { UnblockTenantModal } from './unblock-tenant.modal';

const RESET_PASSWORD_EMAIL_MODAL_TYPE = 'RESET_PASSWORD_EMAIL';
const BLOCK_TENANT_MODAL_TYPE = 'BLOCK_TENANT';
const UNBLOCK_TENANT_MODAL_TYPE = 'UNBLOCK_TENANT';

const RESETTABLE_PASSWORD_USER_TYPES = [
  UserDTOUserTypeIdEnum.TENANT,
  UserDTOUserTypeIdEnum.AGENT,
  UserDTOUserTypeIdEnum.LANDLORD,
];

interface UserViewProps {
  location: any;
  match: any;
}

const UserDetails = styled(RepositCard)`
  width: 100%;
`;

const renderTd = (text: React.ReactNode) => <Td style={{ paddingLeft: 10 }}>{text}</Td>;

const OrgListWrapper = styled.ul`
  background: #fff;
  margin: 22px 0 36px;
  min-width: 200px;
  overflow: hidden;
  padding: 0;
`;

const OrgItem = styled.div`
  background: ${(props) => props.theme.colors.bgAccent};
  margin: 0 0 6px;
  padding: 0 24px 0;

  @media all and (min-width: ${getBreakpoint('lg')}) {
    padding: 0 34px;
  }

  &:hover {
    background: ${(props) => props.theme.colors.bgAccent};
  }
`;

const OrgItemInner = styled.li`
  border-radius: 8px;
  cursor: default;
  box-sizing: border-box;
  margin: 0 0 6px;
  padding: 12px 0;

  @media all and (min-width: ${getBreakpoint('lg')}) {
    align-items: center;
    display: flex;
    justify-content: space-between;
    height: 48px;
    margin: 0 0 6px;
    padding: 0;
  }
`;

const Org = styled.div`
  @media all and (min-width: ${getBreakpoint('lg')}) {
    align-items: center;
    display: flex;
  }
`;

const Name = styled(Header4)`
  font-size: 1em;
  margin: 0;

  @media all and (min-width: ${getBreakpoint('lg')}) {
    margin: 0 10px 0 0;
  }
`;

const ViewLink = styled(NavLink)`
  margin: 0 0 0 auto;
`;

const ActionButton = styled(Button)`
  margin-left: auto;
`;

const UserView: React.FC<UserViewProps> = ({ match }) => {
  const dispatch = useDispatch();
  const userId = match.params.userId;
  useAdminDocumentTitle(userId);
  const user = useSelector((state: AppState) => getUserById(state, { userId }));
  const orgUsers = useSelector((state: AppState) => getOrganizationUsersByUserId(state, userId));
  const fetchUserLoadingSelector = createLoadingSelector([FETCH_USER_BY_ID_STORE_KEY]);
  const isLoading = useSelector(fetchUserLoadingSelector);
  const fetchResetPasswordLoadingSelector = createLoadingSelector([RESET_USER_PASSWORD_STORE_KEY]);
  const isResetPasswordLoading = useSelector(fetchResetPasswordLoadingSelector);
  const blockTenantLoadingSelector = createLoadingSelector([BLOCK_TENANT_STORE_KEY]);
  const isBlockTenantLoading = useSelector(blockTenantLoadingSelector);
  const unblockTenantLoadingSelector = createLoadingSelector([UNBLOCK_TENANT_STORE_KEY]);
  const isUnblockTenantLoading = useSelector(unblockTenantLoadingSelector);
  const isTenantBlocked = useSelector(getIsTenantBlocked);
  const blockedTenant = useSelector(getBlockedTenant);
  const hasFetchedBlockedTenant = useSelector(getHasFetchedBlockedTenant);
  const hasManageBlockedTenantsRole = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.MANAGEBLOCKEDTENANTS),
  );
  const currentModal = useSelector(getCurrentModal);
  useEffect(() => {
    dispatch(fetchUserByIdRequested(userId));
    dispatch(fetchOrganizationUsersByUserIdRequested({ userId }));
  }, [dispatch, userId]);
  const isEditModalOpen = useSelector(getIsEditModalOpen);
  const [flashMessage, onDismissFlashMessage] = useFlashMessage([UPDATE_USER_BY_ID_STORE_KEY]);
  const isTenant = !!(user && user.userTypeId === UserDTOUserTypeIdEnum.TENANT);
  const blockedTenantAction = resolveBlockedTenantAction({
    isTenant,
    hasManageBlockedTenantsRole,
    hasFetchedBlockedTenant,
    isTenantBlocked,
  });
  useEffect(() => {
    if (!user || user.userTypeId !== UserDTOUserTypeIdEnum.TENANT || !hasManageBlockedTenantsRole) {
      return;
    }
    dispatch(fetchBlockedTenantRequested(user.email));
  }, [dispatch, user, hasManageBlockedTenantsRole]);

  if (isLoading || !user) return <FullPageLoader />;

  const { id, firstName, email, userTypeId, lastName, createdAt, attributes, legacyId } = user;
  let mobileNumber;
  let officePhoneNumber;
  let endOfTenancyEmailsDisabled;
  let endOfTenancyContact;
  let jobTitle;
  let dob;
  let gender;

  if (attributes) {
    mobileNumber = attributes.mobileNumber;
    officePhoneNumber = attributes.officePhoneNumber;
    endOfTenancyEmailsDisabled = attributes.endOfTenancyEmailsDisabled;
    endOfTenancyContact = attributes.endOfTenancyContact === undefined ? true : !!attributes.endOfTenancyContact;
    jobTitle = attributes.jobTitle;
    dob = attributes.dob;
    gender = attributes.gender;
  }

  const formatGender = (gender: string) => {
    if (gender) {
      return gender === 'UNKNOWN' ? 'Rather not say' : gender;
    }
  };

  return (
    <Fragment>
      {isEditModalOpen ? <EditUserModal user={user} /> : null}
      {currentModal === RESET_PASSWORD_EMAIL_MODAL_TYPE ? (
        <ResetPasswordModal
          onDismiss={() => dispatch(setCurrentModal(''))}
          email={email}
          userId={id}
          isLoading={isResetPasswordLoading}
        />
      ) : null}
      {currentModal === BLOCK_TENANT_MODAL_TYPE ? (
        <BlockTenantModal onDismiss={() => dispatch(setCurrentModal(''))} email={email} isLoading={isBlockTenantLoading} />
      ) : null}
      {currentModal === UNBLOCK_TENANT_MODAL_TYPE ? (
        <UnblockTenantModal onDismiss={() => dispatch(setCurrentModal(''))} email={email} isLoading={isUnblockTenantLoading} />
      ) : null}
      <Container>
        <Row>
          <Col lg={12}>
            <Header2>
              User #<IdentifierValue>{id}</IdentifierValue>
            </Header2>
            <Caption>{email}</Caption>
            {RESETTABLE_PASSWORD_USER_TYPES.filter((type) => type === userTypeId).length || isTenant ? (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10, columnGap: 10 }}>
                {blockedTenantAction === 'unblock' ? (
                  <Button onClick={() => dispatch(setCurrentModal(UNBLOCK_TENANT_MODAL_TYPE))}>Unblock tenant</Button>
                ) : blockedTenantAction === 'block' ? (
                  <Button onClick={() => dispatch(setCurrentModal(BLOCK_TENANT_MODAL_TYPE))}>Block tenant</Button>
                ) : null}
                {RESETTABLE_PASSWORD_USER_TYPES.filter((type) => type === userTypeId).length ? (
                  <Button onClick={() => dispatch(setCurrentModal(RESET_PASSWORD_EMAIL_MODAL_TYPE))}>
                    {user.hasAuthDisabled ? 'Re-enable & reset password' : 'Reset password'}
                  </Button>
                ) : null}
              </div>
            ) : null}
            {blockedTenantAction === 'unblock' && blockedTenant ? <BlockedTenantBanner blockedTenant={blockedTenant} /> : null}
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <UserDetails
              flush
              title="User details"
              headerContent={
                userTypeId !== UserDTOUserTypeIdEnum.INTERNAL ? (
                  <ActionButton onClick={() => dispatch(setIsEditModalOpen(true))} buttonType={'secondary'}>
                    Edit
                  </ActionButton>
                ) : undefined
              }
              flashMessage={
                flashMessage ? (
                  <FlashMessage onDismiss={onDismissFlashMessage} timeRemaining={5000} payload={flashMessage} />
                ) : undefined
              }
            >
              <Container>
                <Row>
                  <Col lg={12} style={{ padding: 0 }}>
                    <Table>
                      <Tbody>
                        <Tr>
                          <Th width={240}>ID</Th>
                          {renderTd(<IdentifierValue>{id}</IdentifierValue>)}
                        </Tr>
                        <Tr>
                          <Th width={240}>Type</Th>
                          {renderTd(userTypeId)}
                        </Tr>

                        <Tr>
                          <Th>First name</Th>
                          {renderTd(firstName || '-')}
                        </Tr>
                        <Tr>
                          <Th>Last Name</Th>
                          {renderTd(lastName || '-')}
                        </Tr>
                        <Tr>
                          <Th>Email</Th>
                          {renderTd(email)}
                        </Tr>
                        {!isTenant ? (
                          <Tr>
                            <Th>Job Title</Th>
                            {renderTd(jobTitle || '-')}
                          </Tr>
                        ) : null}

                        <Tr>
                          <Th>Mobile Number</Th>
                          {renderTd(mobileNumber || '-')}
                        </Tr>
                        {!isTenant ? (
                          <Tr>
                            <Th>Office Phone Number</Th>
                            {renderTd(officePhoneNumber || '-')}
                          </Tr>
                        ) : null}

                        {legacyId && (
                          <Tr>
                            <Th>Legacy ID</Th>
                            {renderTd(<IdentifierValue>{legacyId}</IdentifierValue>)}
                          </Tr>
                        )}
                        {!isTenant ? (
                          <Tr>
                            <Th>Only subscribed to personal notifications</Th>
                            {renderTd(endOfTenancyContact ? 'False' : 'True')}
                          </Tr>
                        ) : null}
                        {!isTenant ? (
                          <Tr>
                            <Th>End Of Tenancy Notifications Disabled</Th>
                            {renderTd(endOfTenancyEmailsDisabled ? 'True' : 'False')}
                          </Tr>
                        ) : null}
                        {isTenant ? (
                          <Tr>
                            <Th>Date of Birth</Th>
                            {renderTd(dob ? moment(dob, 'YYYY-MM-DD').format('DD/MM/YYYY') : '-')}
                          </Tr>
                        ) : null}
                        {isTenant ? (
                          <Tr>
                            <Th>Gender</Th>
                            {renderTd(gender ? startCase(lowerCase(formatGender(gender))) : '-')}
                          </Tr>
                        ) : null}
                        <Tr>
                          <Th>Registered Since</Th>
                          {renderTd(moment(createdAt).format('DD/MM/YYYY'))}
                        </Tr>
                        <Tr>
                          <Th width={240}>User Locked</Th>
                          {renderTd(user.hasAuthDisabled ? 'True' : 'False')}
                        </Tr>
                      </Tbody>
                    </Table>
                  </Col>
                </Row>
              </Container>
            </UserDetails>
          </Col>
        </Row>
        {orgUsers && orgUsers.length ? (
          <Row>
            <Col>
              <UserDetails title="Organisation(s)">
                <OrgListWrapper>
                  {orgUsers.map((ou) => {
                    const { roles } = ou;
                    const hasAllRoles = roles.length === 4;
                    return (
                      <OrgItem key={ou.id}>
                        <OrgItemInner>
                          <Org>
                            <Name>{ou.organization.name}</Name>
                            {hasAllRoles ? <RolePill>Admin</RolePill> : null}
                          </Org>
                          <ViewLink to={`/organisations/${ou.organizationId}`}>
                            <Button buttonType="secondary" mini="true">
                              View
                            </Button>
                          </ViewLink>
                        </OrgItemInner>
                      </OrgItem>
                    );
                  })}
                </OrgListWrapper>
              </UserDetails>
            </Col>
          </Row>
        ) : null}
      </Container>
    </Fragment>
  );
};

export default UserView;
